import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './AdminFormStyles.css';

interface BannerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  banner?: any | null;
}

const BannerFormModal: React.FC<BannerFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  banner
}) => {
  const [formData, setFormData] = useState({
    title_bn: '',
    title_en: '',
    subtitle_bn: '',
    subtitle_en: '',
    link: '',
    image: null as File | null,
    image_url: '',
    is_active: true,
    display_order: 0
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (banner) {
      setFormData({
        title_bn: banner.title_bn || '',
        title_en: banner.title_en || '',
        subtitle_bn: banner.subtitle_bn || '',
        subtitle_en: banner.subtitle_en || '',
        link: banner.link || '',
        image: null,
        image_url: banner.image || '',
        is_active: banner.is_active ?? true,
        display_order: banner.display_order || 0
      });
    } else {
      setFormData({
        title_bn: '',
        title_en: '',
        subtitle_bn: '',
        subtitle_en: '',
        link: '',
        image: null,
        image_url: '',
        is_active: true,
        display_order: 0
      });
    }
    setErrors({});
  }, [banner, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        image: file,
        image_url: URL.createObjectURL(file)
      }));
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.title_bn.trim()) newErrors.title_bn = 'Bangla title is required';
    if (!formData.title_en.trim()) newErrors.title_en = 'English title is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error: any) {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={banner ? 'Edit Banner' : 'Add Banner'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Title Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Title (Bangla) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="title_bn"
                value={formData.title_bn}
                onChange={handleChange}
                className={errors.title_bn ? 'has-error' : ''}
              />
              {errors.title_bn && <span className="error-text">{errors.title_bn}</span>}
            </div>
          </div>

          {/* Title English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Title (English) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                className={errors.title_en ? 'has-error' : ''}
              />
              {errors.title_en && <span className="error-text">{errors.title_en}</span>}
            </div>
          </div>

          {/* Subtitle Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Subtitle (Bangla)</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="subtitle_bn"
                value={formData.subtitle_bn}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Subtitle English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Subtitle (English)</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="subtitle_en"
                value={formData.subtitle_en}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Image Upload */}
          <div className="admin-form-row">
            <div className="admin-form-label">Banner Image *</div>
            <div className="admin-form-input">
              {formData.image_url && (
                <div className="image-preview" style={{ marginBottom: '10px' }}>
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className={errors.image ? 'has-error' : ''}
              />
              {errors.image && <span className="error-text">{errors.image}</span>}
              <p className="help-text" style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Recommended size: 1200x400px
              </p>
            </div>
          </div>

          {/* Link URL */}
          <div className="admin-form-row">
            <div className="admin-form-label">Link URL</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="link"
                value={formData.link}
                onChange={handleChange}
                placeholder="/products?category=electronics"
              />
              <p className="help-text" style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Enter internal path (e.g., /products) or full URL (e.g., https://example.com)
              </p>
            </div>
          </div>

          {/* Display Order */}
          <div className="admin-form-row">
            <div className="admin-form-label">Display Order</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active Banner</span>
              </label>
            </div>
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal >
  );
};

export default BannerFormModal;
